import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { SitesRepository } from "@/repositories/SitesRepository";

export function useChooseSubdomain() {
  const { user } = useAuth();
  const [linkName, setLinkName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionData, setActionData] = useState<{ error: string | null }>({
    error: null,
  });
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [newSiteId, setNewSiteId] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setActionData({ error: null });

    if (!linkName.trim()) {
      setActionData({ error: "Subdomain cannot be empty." });
      setIsSubmitting(false);
      return;
    }

    if (linkName.length < 4) {
      setActionData({ error: "Subdomain must be at least 4 characters long." });
      setIsSubmitting(false);
      return;
    }

    if (linkName.length > 63) {
      setActionData({ error: "Subdomain cannot exceed 63 characters." });
      setIsSubmitting(false);
      return;
    }

    const subdomainRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!subdomainRegex.test(linkName)) {
      setActionData({
        error:
          "Subdomain can only contain lowercase letters, numbers, and hyphens. It cannot start or end with a hyphen or have consecutive hyphens.",
      });
      setIsSubmitting(false);
      return;
    }

    if (!user) {
      setActionData({
        error: "You must be logged in to create a site.",
      });
      setIsSubmitting(false);
      return;
    }

    const userId = user.id;

    const { data: insertedData, error: insertError } = await SitesRepository.insert({
      subdomain: linkName,
      userId: userId,
    });

    if (insertError) {
      // Check if the error is related to duplicate subdomain
      if (insertError.message?.includes("duplicate key value violates unique constraint") &&
          insertError.message?.includes("site_subdomain_key")) {
        setActionData({ error: "This name is already taken. Please choose a different name for your site." });
      }
      // Check if the error is related to length constraint
      else if (insertError.message?.includes("violates check constraint") &&
               (insertError.message?.includes("sites_subdomain_min_length") || insertError.message?.includes("sites_subdomain_length_check"))) {
        setActionData({ error: "Subdomain must be between 4-63 characters long." });
      }
      // Check if the error is related to reserved subdomain
      else if (insertError.message?.includes("is reserved and cannot be registered")) {
        setActionData({ error: "This subdomain name is reserved and cannot be used. Please choose a different name." });
      }
      // Handle generic database errors more helpfully
      else if (insertError.message?.includes("Database error") || insertError.message?.includes("Failed query")) {
        setActionData({ error: "This name is already taken. Please try a different name." });
      }
      else {
        // For any other error, provide a more user-friendly message while preserving the original for debugging
        console.error('Site creation error:', insertError.message);
        setActionData({ error: "Unable to create your site. Please try a different name or contact support if this continues." });
      }
    } else if (insertedData) {
      setSubmissionSuccess(true);
      setNewSiteId(insertedData.id);
    } else {
      // Handle the case where insertError is null but insertedData is also null (should ideally not happen with .select())
      setActionData({ error: "An unexpected error occurred during site creation." });
    }

    setIsSubmitting(false);
  };

  return {
    linkName,
    setLinkName,
    isSubmitting,
    actionData,
    submissionSuccess,
    handleSubmit,
    newSiteId,
  };
} 